import { supabase } from './supabase';

const GUEST_KEY = 'bitelearning_history';

// ── Save a session ────────────────────────────────────────────────────────────
export async function saveSession({ userId, isGuest, subject, format, aiModel, level, background, durationSeconds = 0 }) {
  const cleanSubject = subject?.trim() || '';
  const entry = {
    subject:             cleanSubject,
    subject_normalized:  cleanSubject.toLowerCase(),
    format,
    ai_model:            aiModel  || null,
    level,
    background:          background || null,
    duration_seconds:    Math.max(0, Math.round(durationSeconds)),
  };

  if (isGuest || !userId) {
    const history = _guestRead();
    history.unshift({ ...entry, id: `guest_${Date.now()}` });
    _guestWrite(history.slice(0, 100)); // keep last 100
    return { data: entry, error: null };
  }

  const { data, error } = await supabase
    .from('learning_sessions')
    .insert({ ...entry, user_id: userId });

  return { data, error };
}

// ── Get recent sessions (all subjects) ───────────────────────────────────────
export async function getRecentHistory({ userId, isGuest, limit = 30 }) {
  if (isGuest || !userId) {
    return { data: _guestRead().slice(0, limit), error: null };
  }

  return await supabase
    .from('learning_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
}

// ── Get sessions for a specific subject (used for adaptive context) ───────────
export async function getSubjectHistory({ userId, isGuest, subject, limit = 10 }) {
  if (!subject) return { data: [], error: null };

  const normalised = subject.trim().toLowerCase();

  if (isGuest || !userId) {
    const matches = _guestRead()
      .filter(s => s.subject?.toLowerCase() === normalised)
      .slice(0, limit);
    return { data: matches, error: null };
  }

  return await supabase
    .from('learning_sessions')
    .select('subject, format, level, duration_seconds, created_at')
    .eq('user_id', userId)
    .eq('subject_normalized', normalised)
    .order('created_at', { ascending: false })
    .limit(limit);
}

// ── Summarise progress on a subject ──────────────────────────────────────────
export function summariseHistory(sessions = []) {
  if (!sessions.length) return null;
  return {
    sessionCount:   sessions.length,
    totalMinutes:   Math.round(sessions.reduce((s, r) => s + (r.duration_seconds || 0), 0) / 60),
    formatsUsed:    [...new Set(sessions.map(r => r.format))],
    lastLevel:      sessions[0]?.level || 'beginner',
    lastStudied:    sessions[0]?.created_at,
  };
}

// ── Guest localStorage helpers ────────────────────────────────────────────────
function _guestRead() {
  try { return JSON.parse(localStorage.getItem(GUEST_KEY) || '[]'); }
  catch { return []; }
}
function _guestWrite(arr) {
  try { localStorage.setItem(GUEST_KEY, JSON.stringify(arr)); } catch {}
}
